/**
 * Layer 2a — fetch public Play Store listing HTML (no official API key).
 * Best-effort; may fail on web CORS or rate limits.
 */

/**
 * @typedef {Object} PlayStoreMetadata
 * @property {string} packageName
 * @property {string} title
 * @property {string} description
 * @property {string} contentRating e.g. "Teen", "Mature 17+"
 * @property {string} [developer]
 */

/**
 * @param {string} packageName
 * @returns {Promise<PlayStoreMetadata|null>}
 */
export async function fetchPlayStoreMetadata(packageName) {
  const pkg = String(packageName || "").trim();
  if (!pkg) return null;

  const url = `https://play.google.com/store/apps/details?id=${encodeURIComponent(pkg)}&hl=en&gl=us`;
  const res = await fetch(url, {
    headers: {
      "User-Agent":
        "Mozilla/5.0 (Linux; Android 10) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Mobile Safari/537.36",
    },
  });
  if (!res.ok) return null;
  const html = await res.text();
  return parsePlayStoreHtml(pkg, html);
}

/**
 * @param {string} packageName
 * @param {string} html
 * @returns {PlayStoreMetadata|null}
 */
export function parsePlayStoreHtml(packageName, html) {
  if (!html) return null;

  const title =
    extractMeta(html, "og:title") ||
    extractBetween(html, '<span class="AfwdI">', "</span>") ||
    packageName;

  const description =
    extractMeta(html, "og:description") ||
    extractJsonLdDescription(html) ||
    "";

  const contentRating =
    extractItemprop(html, "contentRating") ||
    extractBetween(html, 'itemprop="contentRating"', ">") ||
    matchContentRatingLabel(html) ||
    "";

  const developer = extractMeta(html, "app-developer") || "";

  return {
    packageName,
    title: decodeHtml(title).trim(),
    description: decodeHtml(description).trim(),
    contentRating: decodeHtml(contentRating).trim(),
    developer: decodeHtml(developer).trim(),
  };
}

function extractMeta(html, prop) {
  const re = new RegExp(`<meta[^>]+property=["']${prop}["'][^>]+content=["']([^"']+)`, "i");
  const m = re.exec(html);
  return m ? m[1] : "";
}

function extractItemprop(html, name) {
  const re = new RegExp(`itemprop=["']${name}["'][^>]*>([^<]+)<`, "i");
  const m = re.exec(html);
  return m ? m[1] : "";
}

function extractBetween(html, start, end) {
  const i = html.indexOf(start);
  if (i < 0) return "";
  const j = html.indexOf(end, i + start.length);
  if (j < 0) return "";
  return html.slice(i + start.length, j);
}

function extractJsonLdDescription(html) {
  const m = /"description"\s*:\s*"((?:\\.|[^"\\])*)"/i.exec(html);
  return m ? m[1].replace(/\\n/g, " ").replace(/\\"/g, '"') : "";
}

function matchContentRatingLabel(html) {
  const m = /(Everyone|Teen|Everyone 10\+|Mature 17\+|Adults only 18\+|Unrated)/i.exec(html);
  return m ? m[1] : "";
}

function decodeHtml(s) {
  return String(s || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"');
}
